import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Rocket, 
  Plus, 
  Package,
  Clock,
  CheckCircle2,
  AlertCircle,
  Loader2,
  Eye,
  Trash2,
  ExternalLink,
  Link as LinkIcon,
  Store,
  ShoppingCart,
  Globe,
  ChevronDown,
  ChevronUp,
  Search,
  Pencil,
  X,
  Mail,
  Download
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TAX_CODES = ['5%', '12%', '18%', '28%'];
const VEG_OPTIONS = ['Vegetarian', 'Non-Vegetarian'];

const STATUS_CONFIG = {
  pending: { label: 'Pending', icon: Clock, color: 'bg-yellow-100 text-yellow-700 border-yellow-200' },
  live_partial: { label: 'Partially Live', icon: AlertCircle, color: 'bg-blue-100 text-blue-700 border-blue-200' },
  live_all: { label: 'Live on All', icon: CheckCircle2, color: 'bg-green-100 text-green-700 border-green-200' }
};

const PLATFORM_CONFIG = {
  amazon: { label: 'Amazon', icon: ShoppingCart, color: 'text-orange-600 bg-orange-50' },
  flipkart: { label: 'Flipkart', icon: Store, color: 'text-blue-600 bg-blue-50' },
  wellversed: { label: 'Wellversed', icon: Globe, color: 'text-teal-600 bg-teal-50' }
};

// FormSection component defined OUTSIDE to prevent re-creation on every render
const FormSection = ({ title, expanded, onToggle, children }) => (
  <div className="border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
    <button
      type="button"
      onClick={onToggle}
      className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-800 flex items-center justify-between hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
    >
      <span className="font-medium text-slate-700 dark:text-slate-200">{title}</span>
      {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
    </button>
    {expanded && (
      <div className="p-4 space-y-4 bg-white dark:bg-slate-900">
        {children}
      </div>
    )}
  </div>
);

const initialFormData = {
  brand_id: '',
  brand_name: '',
  amazon_title: '',
  flipkart_title: '',
  wellversed_title: '',
  bullet_points: '',
  description: '',
  fssai_flavour_name: '',
  rda: '',
  mrp: '',
  selling_price: '',
  hsn_code: '',
  tax_code: '',
  gross_weight: '',
  net_weight: '',
  product_dimensions: '',
  package_dimensions: '',
  manufacturer_info: '',
  packer_info: '',
  ingredients: '',
  veg_nonveg: '',
  serving_size: '',
  item_form: '',
  shelf_life: '',
  item_type: '',
  product_color: '',
  allergen_info: '',
  flavour: '',
  ean: '',
  sku: '',
  liquid_volume: '',
  liquid_content_description: '',
  notification_emails: [''],
  cc_emails: ['']
};

export default function NewProductLaunchPage() {
  const { isAdmin } = useAuth();
  const [brands, setBrands] = useState([]);
  const [launches, setLaunches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFormModal, setShowFormModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showUrlModal, setShowUrlModal] = useState(false);
  const [selectedLaunch, setSelectedLaunch] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [editLaunchId, setEditLaunchId] = useState(null);
  const [expandedSections, setExpandedSections] = useState({
    titles: true,
    basic: false,
    specifications: false,
    packaging: false,
    compliance: false,
    notifications: false,
    optional: false
  });
  
  // URL update state
  const [urlPlatform, setUrlPlatform] = useState('');
  const [urlValue, setUrlValue] = useState('');
  
  // Form state
  const [formData, setFormData] = useState(initialFormData);

  useEffect(() => {
    fetchBrands();
    fetchLaunches();
  }, []);

  const fetchBrands = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const response = await fetch(`${API_URL}/api/masters/brands`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setBrands(data);
      }
    } catch (error) {
      console.error('Error fetching brands:', error);
    }
  };

  const fetchLaunches = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('session_token');
      const response = await fetch(`${API_URL}/api/product-launches`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setLaunches(data);
      }
    } catch (error) {
      console.error('Error fetching launches:', error);
      toast.error('Failed to fetch product launches');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleBrandChange = (brandId) => {
    const brand = brands.find(b => b.id === brandId);
    setFormData(prev => ({
      ...prev,
      brand_id: brandId,
      brand_name: brand?.name || '',
      // Auto-fill notification email with brand manager email
      notification_emails: brand?.manager_email ? [brand.manager_email] : prev.notification_emails
    }));
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // Handle notification email changes
  const handleNotificationEmailChange = (index, value) => {
    setFormData(prev => {
      const newEmails = [...prev.notification_emails];
      newEmails[index] = value;
      return { ...prev, notification_emails: newEmails };
    });
  };

  const addNotificationEmail = () => {
    setFormData(prev => ({
      ...prev,
      notification_emails: [...prev.notification_emails, '']
    }));
  };

  const removeNotificationEmail = (index) => {
    setFormData(prev => ({
      ...prev,
      notification_emails: prev.notification_emails.filter((_, i) => i !== index)
    }));
  };

  // Handle CC email changes
  const handleCcEmailChange = (index, value) => {
    setFormData(prev => {
      const newEmails = [...prev.cc_emails];
      newEmails[index] = value;
      return { ...prev, cc_emails: newEmails };
    });
  };

  const addCcEmail = () => {
    setFormData(prev => ({
      ...prev,
      cc_emails: [...prev.cc_emails, '']
    }));
  };

  const removeCcEmail = (index) => {
    setFormData(prev => ({
      ...prev,
      cc_emails: prev.cc_emails.filter((_, i) => i !== index)
    }));
  };

  // Open form for editing
  const handleEdit = (launch) => {
    setEditMode(true);
    setEditLaunchId(launch.id);
    setFormData({
      brand_id: launch.brand_id || '',
      brand_name: launch.brand_name || '',
      amazon_title: launch.amazon_title || '',
      flipkart_title: launch.flipkart_title || '',
      wellversed_title: launch.wellversed_title || '',
      bullet_points: launch.bullet_points || '',
      description: launch.description || '',
      fssai_flavour_name: launch.fssai_flavour_name || '',
      rda: launch.rda || '',
      mrp: launch.mrp || '',
      selling_price: launch.selling_price || '',
      hsn_code: launch.hsn_code || '',
      tax_code: launch.tax_code || '',
      gross_weight: launch.gross_weight || '',
      net_weight: launch.net_weight || '',
      product_dimensions: launch.product_dimensions || '',
      package_dimensions: launch.package_dimensions || '',
      manufacturer_info: launch.manufacturer_info || '',
      packer_info: launch.packer_info || '',
      ingredients: launch.ingredients || '',
      veg_nonveg: launch.veg_nonveg || '',
      serving_size: launch.serving_size || '',
      item_form: launch.item_form || '',
      shelf_life: launch.shelf_life || '',
      item_type: launch.item_type || '',
      product_color: launch.product_color || '',
      allergen_info: launch.allergen_info || '',
      flavour: launch.flavour || '',
      ean: launch.ean || '',
      sku: launch.sku || '',
      liquid_volume: launch.liquid_volume || '',
      liquid_content_description: launch.liquid_content_description || '',
      notification_emails: launch.notification_emails?.length > 0 ? launch.notification_emails : [''],
      cc_emails: launch.cc_emails?.length > 0 ? launch.cc_emails : ['']
    });
    setShowFormModal(true);
  };

  // Reset form and close modal
  const handleCloseForm = () => {
    setShowFormModal(false);
    setEditMode(false);
    setEditLaunchId(null);
    setFormData(initialFormData);
  };

  const validateForm = () => {
    const requiredFields = [
      'brand_id', 'amazon_title', 'flipkart_title', 'wellversed_title',
      'bullet_points', 'description', 'fssai_flavour_name', 'rda',
      'mrp', 'selling_price', 'hsn_code', 'tax_code', 'gross_weight',
      'net_weight', 'product_dimensions', 'package_dimensions',
      'manufacturer_info', 'packer_info', 'ingredients', 'veg_nonveg',
      'serving_size', 'item_form', 'shelf_life', 'item_type',
      'product_color', 'allergen_info', 'flavour', 'ean', 'sku'
    ];
    
    for (const field of requiredFields) {
      if (!formData[field] || formData[field].trim() === '') {
        const fieldLabel = field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
        toast.error(`${fieldLabel} is required`);
        return false;
      }
    }
    
    // Validate HSN code (6 digits)
    if (!/^\d{6}$/.test(formData.hsn_code)) {
      toast.error('HSN Code must be exactly 6 digits');
      return false;
    }
    
    // Validate EAN (digits only)
    if (!/^\d+$/.test(formData.ean)) {
      toast.error('EAN must contain only digits');
      return false;
    }
    
    // Validate at least one notification email
    const validNotificationEmails = formData.notification_emails.filter(e => e.trim());
    if (validNotificationEmails.length === 0) {
      toast.error('At least one notification recipient is required');
      return false;
    }
    
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    try {
      const token = localStorage.getItem('session_token');
      
      // Filter out empty emails
      const submitData = {
        ...formData,
        notification_emails: formData.notification_emails.filter(e => e.trim()),
        cc_emails: formData.cc_emails.filter(e => e.trim())
      };
      
      const url = editMode 
        ? `${API_URL}/api/product-launches/${editLaunchId}`
        : `${API_URL}/api/product-launches`;
      
      const response = await fetch(url, {
        method: editMode ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(submitData)
      });
      
      if (response.ok) {
        toast.success(editMode ? 'Product launch updated successfully!' : 'Product launch submitted successfully!');
        handleCloseForm();
        fetchLaunches();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to submit product launch');
      }
    } catch (error) {
      console.error('Error submitting launch:', error);
      toast.error('Failed to submit product launch');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateUrl = async () => {
    if (!urlPlatform || !urlValue.trim()) {
      toast.error('Please select platform and enter URL');
      return;
    }
    
    try {
      const token = localStorage.getItem('session_token');
      const response = await fetch(
        `${API_URL}/api/product-launches/${selectedLaunch.id}/platform-url?platform=${urlPlatform}&url=${encodeURIComponent(urlValue)}`,
        {
          method: 'PATCH',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      if (response.ok) {
        toast.success(`${PLATFORM_CONFIG[urlPlatform].label} URL updated!`);
        setShowUrlModal(false);
        setUrlPlatform('');
        setUrlValue('');
        fetchLaunches();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update URL');
      }
    } catch (error) {
      console.error('Error updating URL:', error);
      toast.error('Failed to update URL');
    }
  };

  const handleDelete = async (launchId) => {
    if (!window.confirm('Are you sure you want to delete this product launch?')) return;
    
    try {
      const token = localStorage.getItem('session_token');
      const response = await fetch(`${API_URL}/api/product-launches/${launchId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success('Product launch deleted');
        fetchLaunches();
      } else {
        toast.error('Failed to delete product launch');
      }
    } catch (error) {
      console.error('Error deleting launch:', error);
      toast.error('Failed to delete product launch');
    }
  };

  const filteredLaunches = launches.filter(launch => {
    const matchesTab = activeTab === 'all' || launch.status === activeTab;
    const matchesSearch = !searchQuery || 
      launch.sku?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      launch.brand_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      launch.amazon_title?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesTab && matchesSearch;
  });

  // Export filtered submissions to CSV (respects active tab + search)
  const exportLaunches = () => {
    if (filteredLaunches.length === 0) { toast.error('Nothing to export'); return; }
    const STATUS_LABEL = {
      pending: 'Pending',
      live_partial: 'Partially Live',
      live_all: 'Live on All',
    };
    const escape = (val) => {
      if (val == null) return '';
      const s = String(val).replace(/"/g, '""');
      return /[",\n\r]/.test(s) ? `"${s}"` : s;
    };
    const fmt = (d) => d ? new Date(d).toISOString().slice(0, 19).replace('T', ' ') : '';
    const headers = [
      'SKU', 'Brand', 'Submitted At', 'Submitted By', 'Status',
      'Amazon Title', 'Flipkart Title', 'Quick Commerce Title',
      'Amazon URL', 'Flipkart URL', 'Quick Commerce URL',
      'Ingredients', 'Last Updated',
    ];
    const rows = filteredLaunches.map(l => [
      l.sku, l.brand_name, fmt(l.submitted_at), l.submitted_by,
      STATUS_LABEL[l.status] || l.status,
      l.amazon_title, l.flipkart_title, l.quick_commerce_title,
      l.amazon_url, l.flipkart_url, l.quick_commerce_url,
      l.ingredients, fmt(l.updated_at),
    ].map(escape).join(','));
    const csv = [headers.join(','), ...rows].join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const date = new Date().toISOString().slice(0, 10);
    a.href = url;
    a.download = `product-launches-${activeTab}-${date}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(`Exported ${filteredLaunches.length} submission${filteredLaunches.length === 1 ? '' : 's'}`);
  };

  const renderStatusBadge = (status) => {
    const config = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
    const Icon = config.icon;
    return (
      <Badge variant="outline" className={`${config.color} border`}>
        <Icon className="h-3 w-3 mr-1" />
        {config.label}
      </Badge>
    );
  };

  const renderPlatformUrl = (launch, platform) => {
    const url = launch[`${platform}_url`];
    const config = PLATFORM_CONFIG[platform];
    const Icon = config.icon;
    
    if (url) {
      return (
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${config.color} hover:opacity-80 transition-opacity`}
        >
          <Icon className="h-3 w-3" />
          <ExternalLink className="h-3 w-3" />
        </a>
      );
    }
    
    return (
      <span className="text-slate-400 text-xs">-</span>
    );
  };

  return (
    <div className="p-6 space-y-6" data-testid="new-product-launch-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <Rocket className="h-6 w-6 text-teal-500" />
            New Product Launch
          </h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1">
            Submit product details for listing on Amazon, Flipkart & Wellversed
          </p>
        </div>
        <Button 
          onClick={() => setShowFormModal(true)}
          className="bg-teal-600 hover:bg-teal-700"
          data-testid="new-launch-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          New Product Launch
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-800 dark:to-slate-900 border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Submissions</p>
                <p className="text-2xl font-bold text-slate-900 dark:text-white">{launches.length}</p>
              </div>
              <Package className="h-8 w-8 text-slate-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-yellow-50 to-amber-50 dark:from-yellow-900/20 dark:to-amber-900/20 border-yellow-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-yellow-600">Pending</p>
                <p className="text-2xl font-bold text-yellow-700">{launches.filter(l => l.status === 'pending').length}</p>
              </div>
              <Clock className="h-8 w-8 text-yellow-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-blue-600">Partially Live</p>
                <p className="text-2xl font-bold text-blue-700">{launches.filter(l => l.status === 'live_partial').length}</p>
              </div>
              <AlertCircle className="h-8 w-8 text-blue-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border-green-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-green-600">Live on All</p>
                <p className="text-2xl font-bold text-green-700">{launches.filter(l => l.status === 'live_all').length}</p>
              </div>
              <CheckCircle2 className="h-8 w-8 text-green-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters & Search */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full sm:w-auto">
          <TabsList>
            <TabsTrigger value="all">All</TabsTrigger>
            <TabsTrigger value="pending">Pending</TabsTrigger>
            <TabsTrigger value="live_partial">Partial</TabsTrigger>
            <TabsTrigger value="live_all">Live</TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="flex items-center gap-2 w-full sm:w-auto">
          <div className="relative flex-1 sm:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Search by SKU, brand..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
          <Button
            variant="outline"
            onClick={exportLaunches}
            disabled={filteredLaunches.length === 0}
            className="shrink-0"
            data-testid="launchpad-export-btn"
            title={filteredLaunches.length === 0 ? 'Nothing to export' : `Export ${filteredLaunches.length} submission${filteredLaunches.length === 1 ? '' : 's'} as CSV`}
          >
            <Download className="h-4 w-4 mr-1.5" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Launch Log Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-teal-500" />
            </div>
          ) : filteredLaunches.length === 0 ? (
            <div className="text-center py-12">
              <Package className="h-12 w-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">No product launches found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50 dark:bg-slate-800">
                    <TableHead className="font-semibold">SKU</TableHead>
                    <TableHead className="font-semibold">Brand</TableHead>
                    <TableHead className="font-semibold">Product (Amazon Title)</TableHead>
                    <TableHead className="font-semibold">EAN</TableHead>
                    <TableHead className="font-semibold">MRP</TableHead>
                    <TableHead className="font-semibold text-center">Platform Links</TableHead>
                    <TableHead className="font-semibold">Status</TableHead>
                    <TableHead className="font-semibold">Submitted</TableHead>
                    <TableHead className="font-semibold text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredLaunches.map((launch) => (
                    <TableRow key={launch.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                      <TableCell className="font-mono text-sm font-medium">{launch.sku}</TableCell>
                      <TableCell>{launch.brand_name}</TableCell>
                      <TableCell className="max-w-[200px]">
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="truncate block">{launch.amazon_title}</span>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="max-w-md">
                              <p>{launch.amazon_title}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </TableCell>
                      <TableCell className="font-mono text-sm">{launch.ean}</TableCell>
                      <TableCell>₹{launch.mrp}</TableCell>
                      <TableCell>
                        <div className="flex items-center justify-center gap-2">
                          {renderPlatformUrl(launch, 'amazon')}
                          {renderPlatformUrl(launch, 'flipkart')}
                          {renderPlatformUrl(launch, 'wellversed')}
                        </div>
                      </TableCell>
                      <TableCell>{renderStatusBadge(launch.status)}</TableCell>
                      <TableCell className="text-sm text-slate-500">
                        {new Date(launch.submitted_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center justify-end gap-1">
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8"
                                  onClick={() => { setSelectedLaunch(launch); setShowViewModal(true); }}
                                >
                                  <Eye className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>View Details</TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8 text-amber-600"
                                  onClick={() => handleEdit(launch)}
                                >
                                  <Pencil className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Edit</TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8 text-blue-600"
                                  onClick={() => { setSelectedLaunch(launch); setShowUrlModal(true); }}
                                >
                                  <LinkIcon className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Add Platform URL</TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8 text-red-600"
                                  onClick={() => handleDelete(launch.id)}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Delete</TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* New Product Launch Form Modal */}
      <Dialog open={showFormModal} onOpenChange={handleCloseForm}>
        <DialogContent className="max-w-4xl max-h-[90vh] p-0 flex flex-col">
          <DialogHeader className="px-6 pt-6 pb-4 border-b">
            <DialogTitle className="flex items-center gap-2">
              {editMode ? <Pencil className="h-5 w-5 text-amber-500" /> : <Rocket className="h-5 w-5 text-teal-500" />}
              {editMode ? 'Edit Product Launch' : 'New Product Launch'}
            </DialogTitle>
            <DialogDescription>
              {editMode ? 'Update the product details' : 'Fill in all product details to submit for listing on all platforms'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto px-6 py-4" style={{ maxHeight: 'calc(90vh - 180px)' }}>
            <div className="space-y-4">
              {/* Brand Selection */}
              <div className="space-y-2">
                <Label>Brand *</Label>
                <Select value={formData.brand_id} onValueChange={handleBrandChange}>
                  <SelectTrigger data-testid="brand-select">
                    <SelectValue placeholder="Select brand" />
                  </SelectTrigger>
                  <SelectContent>
                    {brands.map(brand => (
                      <SelectItem key={brand.id} value={brand.id}>{brand.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Platform Titles Section */}
              <FormSection 
                title="Platform Titles" 
                expanded={expandedSections.titles} 
                onToggle={() => toggleSection('titles')}
              >
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <ShoppingCart className="h-4 w-4 text-orange-500" />
                      Amazon Title *
                    </Label>
                    <Input
                      value={formData.amazon_title}
                      onChange={(e) => handleInputChange('amazon_title', e.target.value)}
                      placeholder="Product title for Amazon listing"
                      data-testid="amazon-title-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Store className="h-4 w-4 text-blue-500" />
                      Flipkart Title *
                    </Label>
                    <Input
                      value={formData.flipkart_title}
                      onChange={(e) => handleInputChange('flipkart_title', e.target.value)}
                      placeholder="Product title for Flipkart listing"
                      data-testid="flipkart-title-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Globe className="h-4 w-4 text-teal-500" />
                      Wellversed Title *
                    </Label>
                    <Input
                      value={formData.wellversed_title}
                      onChange={(e) => handleInputChange('wellversed_title', e.target.value)}
                      placeholder="Product title for Wellversed.in listing"
                      data-testid="wellversed-title-input"
                    />
                  </div>
                </div>
              </FormSection>

              {/* Basic Information Section */}
              <FormSection 
                title="Basic Information" 
                expanded={expandedSections.basic} 
                onToggle={() => toggleSection('basic')}
              >
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2 space-y-2">
                    <Label>Bullet Points *</Label>
                    <Textarea
                      value={formData.bullet_points}
                      onChange={(e) => handleInputChange('bullet_points', e.target.value)}
                      placeholder="Enter bullet points (one per line)"
                      rows={4}
                    />
                  </div>
                  <div className="col-span-2 space-y-2">
                    <Label>Description *</Label>
                    <Textarea
                      value={formData.description}
                      onChange={(e) => handleInputChange('description', e.target.value)}
                      placeholder="Product description"
                      rows={4}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>SKU *</Label>
                    <Input
                      value={formData.sku}
                      onChange={(e) => handleInputChange('sku', e.target.value)}
                      placeholder="e.g., WV-PRO-001"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>EAN *</Label>
                    <Input
                      value={formData.ean}
                      onChange={(e) => handleInputChange('ean', e.target.value)}
                      placeholder="13-digit EAN code"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Flavour *</Label>
                    <Input
                      value={formData.flavour}
                      onChange={(e) => handleInputChange('flavour', e.target.value)}
                      placeholder="e.g., Chocolate, Vanilla"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Item Type *</Label>
                    <Input
                      value={formData.item_type}
                      onChange={(e) => handleInputChange('item_type', e.target.value)}
                      placeholder="e.g., Protein Powder"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Item Form *</Label>
                    <Input
                      value={formData.item_form}
                      onChange={(e) => handleInputChange('item_form', e.target.value)}
                      placeholder="e.g., Powder, Capsule"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Product Color *</Label>
                    <Input
                      value={formData.product_color}
                      onChange={(e) => handleInputChange('product_color', e.target.value)}
                      placeholder="e.g., Brown, White"
                    />
                  </div>
                </div>
              </FormSection>

              {/* Specifications Section */}
              <FormSection 
                title="Pricing & Specifications" 
                expanded={expandedSections.specifications} 
                onToggle={() => toggleSection('specifications')}
              >
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>MRP (₹) *</Label>
                    <Input
                      value={formData.mrp}
                      onChange={(e) => handleInputChange('mrp', e.target.value)}
                      placeholder="e.g., 1999"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Selling Price (₹) *</Label>
                    <Input
                      value={formData.selling_price}
                      onChange={(e) => handleInputChange('selling_price', e.target.value)}
                      placeholder="e.g., 1499"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>HSN Code *</Label>
                    <Input
                      value={formData.hsn_code}
                      onChange={(e) => handleInputChange('hsn_code', e.target.value)}
                      placeholder="6-digit HSN code"
                      maxLength={6}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Tax Code *</Label>
                    <Select value={formData.tax_code} onValueChange={(v) => handleInputChange('tax_code', v)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select tax rate" />
                      </SelectTrigger>
                      <SelectContent>
                        {TAX_CODES.map(rate => (
                          <SelectItem key={rate} value={rate}>{rate}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Gross Weight (g) *</Label>
                    <Input
                      value={formData.gross_weight}
                      onChange={(e) => handleInputChange('gross_weight', e.target.value)}
                      placeholder="e.g., 1200"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Net Weight (g) *</Label>
                    <Input
                      value={formData.net_weight}
                      onChange={(e) => handleInputChange('net_weight', e.target.value)}
                      placeholder="e.g., 1000"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Serving Size *</Label>
                    <Input
                      value={formData.serving_size}
                      onChange={(e) => handleInputChange('serving_size', e.target.value)}
                      placeholder="e.g., 30g"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Shelf Life *</Label>
                    <Input
                      value={formData.shelf_life}
                      onChange={(e) => handleInputChange('shelf_life', e.target.value)}
                      placeholder="e.g., 24 months"
                    />
                  </div>
                </div>
              </FormSection>

              {/* Packaging Section */}
              <FormSection 
                title="Packaging & Dimensions" 
                expanded={expandedSections.packaging} 
                onToggle={() => toggleSection('packaging')}
              >
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Product Dimensions (LxBxH) *</Label>
                    <Input
                      value={formData.product_dimensions}
                      onChange={(e) => handleInputChange('product_dimensions', e.target.value)}
                      placeholder="e.g., 10x10x20 cm"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Package Dimensions (LxBxH) *</Label>
                    <Input
                      value={formData.package_dimensions}
                      onChange={(e) => handleInputChange('package_dimensions', e.target.value)}
                      placeholder="e.g., 12x12x25 cm"
                    />
                  </div>
                  <div className="col-span-2 space-y-2">
                    <Label>Manufacturer Information *</Label>
                    <Textarea
                      value={formData.manufacturer_info}
                      onChange={(e) => handleInputChange('manufacturer_info', e.target.value)}
                      placeholder="Full manufacturer details"
                      rows={2}
                    />
                  </div>
                  <div className="col-span-2 space-y-2">
                    <Label>Packer Information *</Label>
                    <Textarea
                      value={formData.packer_info}
                      onChange={(e) => handleInputChange('packer_info', e.target.value)}
                      placeholder="Full packer details"
                      rows={2}
                    />
                  </div>
                </div>
              </FormSection>

              {/* Compliance Section */}
              <FormSection 
                title="Compliance & Nutrition" 
                expanded={expandedSections.compliance} 
                onToggle={() => toggleSection('compliance')}
              >
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>FSSAI (Flavour Name) *</Label>
                    <Input
                      value={formData.fssai_flavour_name}
                      onChange={(e) => handleInputChange('fssai_flavour_name', e.target.value)}
                      placeholder="Flavour name as in FSSAI certificate"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>RDA *</Label>
                    <Input
                      value={formData.rda}
                      onChange={(e) => handleInputChange('rda', e.target.value)}
                      placeholder="Recommended Dietary Allowance"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Vegetarian/Non-Vegetarian *</Label>
                    <Select value={formData.veg_nonveg} onValueChange={(v) => handleInputChange('veg_nonveg', v)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        {VEG_OPTIONS.map(opt => (
                          <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Allergen Information *</Label>
                    <Input
                      value={formData.allergen_info}
                      onChange={(e) => handleInputChange('allergen_info', e.target.value)}
                      placeholder="e.g., Contains milk, soy"
                    />
                  </div>
                  <div className="col-span-2 space-y-2">
                    <Label>Ingredients *</Label>
                    <Textarea
                      value={formData.ingredients}
                      onChange={(e) => handleInputChange('ingredients', e.target.value)}
                      placeholder="Full list of ingredients"
                      rows={3}
                    />
                  </div>
                </div>
              </FormSection>

              {/* Optional Fields Section */}
              <FormSection 
                title="Optional Fields (If Applicable)" 
                expanded={expandedSections.optional} 
                onToggle={() => toggleSection('optional')}
              >
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Liquid Volume</Label>
                    <Input
                      value={formData.liquid_volume}
                      onChange={(e) => handleInputChange('liquid_volume', e.target.value)}
                      placeholder="e.g., 500ml"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Liquid Content Description</Label>
                    <Input
                      value={formData.liquid_content_description}
                      onChange={(e) => handleInputChange('liquid_content_description', e.target.value)}
                      placeholder="Description of liquid content"
                    />
                  </div>
                </div>
              </FormSection>

              {/* Notification Recipients Section */}
              <div className="border-2 border-dashed border-teal-300 rounded-lg p-1 bg-teal-50/30">
                <FormSection 
                  title="Notification Recipients" 
                  expanded={expandedSections.notifications} 
                  onToggle={() => toggleSection('notifications')}
                >
                <div className="space-y-4">
                  {/* Notification Emails */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <Label className="flex items-center gap-2">
                          <Mail className="h-4 w-4 text-teal-500" />
                          Notification Recipients *
                        </Label>
                        <p className="text-sm text-slate-500">These emails will be notified on submission</p>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={addNotificationEmail}
                        className="text-teal-600 hover:text-teal-700"
                      >
                        <Plus className="h-4 w-4 mr-1" />
                        Add Email
                      </Button>
                    </div>
                    {formData.notification_emails.map((email, index) => (
                      <div key={index} className="flex gap-2">
                        <Input
                          type="email"
                          value={email}
                          onChange={(e) => handleNotificationEmailChange(index, e.target.value)}
                          placeholder="recipient@company.com"
                        />
                        {formData.notification_emails.length > 1 && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => removeNotificationEmail(index)}
                            className="text-slate-400 hover:text-red-500"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* CC Emails */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <Label className="flex items-center gap-2">
                          <Mail className="h-4 w-4 text-slate-400" />
                          CC Recipients
                        </Label>
                        <p className="text-sm text-slate-500">These emails will be CC'd on the notification</p>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={addCcEmail}
                        className="text-teal-600 hover:text-teal-700"
                      >
                        <Plus className="h-4 w-4 mr-1" />
                        Add CC
                      </Button>
                    </div>
                    {formData.cc_emails.map((email, index) => (
                      <div key={index} className="flex gap-2">
                        <Input
                          type="email"
                          value={email}
                          onChange={(e) => handleCcEmailChange(index, e.target.value)}
                          placeholder="cc@company.com"
                        />
                        {formData.cc_emails.length > 1 && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => removeCcEmail(index)}
                            className="text-slate-400 hover:text-red-500"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </FormSection>
              </div>
            </div>
          </div>
          
          <DialogFooter className="border-t px-6 py-4">
            <Button variant="outline" onClick={handleCloseForm}>
              Cancel
            </Button>
            <Button 
              onClick={handleSubmit} 
              disabled={isSubmitting}
              className={editMode ? "bg-amber-600 hover:bg-amber-700" : "bg-teal-600 hover:bg-teal-700"}
              data-testid="submit-launch-btn"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {editMode ? 'Updating...' : 'Submitting...'}
                </>
              ) : (
                <>
                  {editMode ? <Pencil className="h-4 w-4 mr-2" /> : <Rocket className="h-4 w-4 mr-2" />}
                  {editMode ? 'Update Product Launch' : 'Submit Product Launch'}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Details Modal */}
      <Dialog open={showViewModal} onOpenChange={setShowViewModal}>
        <DialogContent className="max-w-3xl h-[85vh] !p-0 !gap-0 overflow-hidden flex flex-col">
          <DialogHeader className="px-6 pt-6 pb-3 border-b shrink-0">
            <DialogTitle className="flex items-center gap-2">
              <Package className="h-5 w-5 text-teal-500" />
              Product Launch Details
            </DialogTitle>
          </DialogHeader>

          {selectedLaunch && (
            <div className="flex-1 min-h-0 overflow-y-auto px-6 py-4">
              <div className="space-y-6">
                {/* Status and Meta */}
                <div className="flex items-center justify-between">
                  {renderStatusBadge(selectedLaunch.status)}
                  <span className="text-sm text-slate-500">
                    Submitted: {new Date(selectedLaunch.submitted_at).toLocaleString()}
                  </span>
                </div>

                {/* Platform Titles */}
                <div className="space-y-3">
                  <h4 className="font-semibold text-slate-900 dark:text-white">Platform Titles</h4>
                  <div className="grid gap-2">
                    <div className="flex items-start gap-2 p-3 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
                      <ShoppingCart className="h-4 w-4 text-orange-600 mt-0.5" />
                      <div>
                        <p className="text-xs text-orange-600 font-medium">Amazon</p>
                        <p className="text-sm">{selectedLaunch.amazon_title}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-2 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                      <Store className="h-4 w-4 text-blue-600 mt-0.5" />
                      <div>
                        <p className="text-xs text-blue-600 font-medium">Flipkart</p>
                        <p className="text-sm">{selectedLaunch.flipkart_title}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-2 p-3 bg-teal-50 dark:bg-teal-900/20 rounded-lg">
                      <Globe className="h-4 w-4 text-teal-600 mt-0.5" />
                      <div>
                        <p className="text-xs text-teal-600 font-medium">Wellversed</p>
                        <p className="text-sm">{selectedLaunch.wellversed_title}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Platform URLs */}
                <div className="space-y-3">
                  <h4 className="font-semibold text-slate-900 dark:text-white">Platform URLs</h4>
                  <div className="grid gap-2">
                    {['amazon', 'flipkart', 'wellversed'].map(platform => {
                      const url = selectedLaunch[`${platform}_url`];
                      const config = PLATFORM_CONFIG[platform];
                      const Icon = config.icon;
                      return (
                        <div key={platform} className="flex items-center justify-between p-3 border rounded-lg">
                          <div className="flex items-center gap-2">
                            <Icon className={`h-4 w-4 ${config.color.split(' ')[0]}`} />
                            <span className="font-medium">{config.label}</span>
                          </div>
                          {url ? (
                            <a
                              href={url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sm text-blue-600 hover:underline flex items-center gap-1"
                            >
                              View Listing <ExternalLink className="h-3 w-3" />
                            </a>
                          ) : (
                            <span className="text-sm text-slate-400">Not yet live</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Product Details Grid */}
                <div className="space-y-3">
                  <h4 className="font-semibold text-slate-900 dark:text-white">Product Information</h4>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div className="p-2 bg-slate-50 dark:bg-slate-800 rounded">
                      <p className="text-slate-500 text-xs">SKU</p>
                      <p className="font-mono font-medium">{selectedLaunch.sku}</p>
                    </div>
                    <div className="p-2 bg-slate-50 dark:bg-slate-800 rounded">
                      <p className="text-slate-500 text-xs">EAN</p>
                      <p className="font-mono font-medium">{selectedLaunch.ean}</p>
                    </div>
                    <div className="p-2 bg-slate-50 dark:bg-slate-800 rounded">
                      <p className="text-slate-500 text-xs">Brand</p>
                      <p className="font-medium">{selectedLaunch.brand_name}</p>
                    </div>
                    <div className="p-2 bg-slate-50 dark:bg-slate-800 rounded">
                      <p className="text-slate-500 text-xs">Flavour</p>
                      <p className="font-medium">{selectedLaunch.flavour}</p>
                    </div>
                    <div className="p-2 bg-slate-50 dark:bg-slate-800 rounded">
                      <p className="text-slate-500 text-xs">MRP</p>
                      <p className="font-medium">₹{selectedLaunch.mrp}</p>
                    </div>
                    <div className="p-2 bg-slate-50 dark:bg-slate-800 rounded">
                      <p className="text-slate-500 text-xs">Selling Price</p>
                      <p className="font-medium">₹{selectedLaunch.selling_price}</p>
                    </div>
                    <div className="p-2 bg-slate-50 dark:bg-slate-800 rounded">
                      <p className="text-slate-500 text-xs">Gross Weight</p>
                      <p className="font-medium">{selectedLaunch.gross_weight}g</p>
                    </div>
                    <div className="p-2 bg-slate-50 dark:bg-slate-800 rounded">
                      <p className="text-slate-500 text-xs">Net Weight</p>
                      <p className="font-medium">{selectedLaunch.net_weight}g</p>
                    </div>
                    <div className="p-2 bg-slate-50 dark:bg-slate-800 rounded">
                      <p className="text-slate-500 text-xs">HSN Code</p>
                      <p className="font-mono font-medium">{selectedLaunch.hsn_code}</p>
                    </div>
                    <div className="p-2 bg-slate-50 dark:bg-slate-800 rounded">
                      <p className="text-slate-500 text-xs">Tax Code</p>
                      <p className="font-medium">{selectedLaunch.tax_code}</p>
                    </div>
                    <div className="p-2 bg-slate-50 dark:bg-slate-800 rounded">
                      <p className="text-slate-500 text-xs">Veg/Non-Veg</p>
                      <p className="font-medium">{selectedLaunch.veg_nonveg}</p>
                    </div>
                    <div className="p-2 bg-slate-50 dark:bg-slate-800 rounded">
                      <p className="text-slate-500 text-xs">Shelf Life</p>
                      <p className="font-medium">{selectedLaunch.shelf_life}</p>
                    </div>
                    <div className="p-2 bg-slate-50 dark:bg-slate-800 rounded">
                      <p className="text-slate-500 text-xs">Item Type</p>
                      <p className="font-medium">{selectedLaunch.item_type || '—'}</p>
                    </div>
                    <div className="p-2 bg-slate-50 dark:bg-slate-800 rounded">
                      <p className="text-slate-500 text-xs">Item Form</p>
                      <p className="font-medium">{selectedLaunch.item_form || '—'}</p>
                    </div>
                    <div className="p-2 bg-slate-50 dark:bg-slate-800 rounded">
                      <p className="text-slate-500 text-xs">Product Colour</p>
                      <p className="font-medium">{selectedLaunch.product_color || '—'}</p>
                    </div>
                    <div className="p-2 bg-slate-50 dark:bg-slate-800 rounded">
                      <p className="text-slate-500 text-xs">Serving Size</p>
                      <p className="font-medium">{selectedLaunch.serving_size || '—'}</p>
                    </div>
                    <div className="p-2 bg-slate-50 dark:bg-slate-800 rounded">
                      <p className="text-slate-500 text-xs">Product Dimensions</p>
                      <p className="font-medium">{selectedLaunch.product_dimensions || '—'}</p>
                    </div>
                    <div className="p-2 bg-slate-50 dark:bg-slate-800 rounded">
                      <p className="text-slate-500 text-xs">Package Dimensions</p>
                      <p className="font-medium">{selectedLaunch.package_dimensions || '—'}</p>
                    </div>
                    <div className="p-2 bg-slate-50 dark:bg-slate-800 rounded">
                      <p className="text-slate-500 text-xs">Liquid Volume</p>
                      <p className="font-medium">{selectedLaunch.liquid_volume || '—'}</p>
                    </div>
                    <div className="p-2 bg-slate-50 dark:bg-slate-800 rounded col-span-2">
                      <p className="text-slate-500 text-xs">Liquid Content Description</p>
                      <p className="font-medium">{selectedLaunch.liquid_content_description || '—'}</p>
                    </div>
                    <div className="p-2 bg-slate-50 dark:bg-slate-800 rounded">
                      <p className="text-slate-500 text-xs">FSSAI Flavour Name</p>
                      <p className="font-medium">{selectedLaunch.fssai_flavour_name || '—'}</p>
                    </div>
                    <div className="p-2 bg-slate-50 dark:bg-slate-800 rounded">
                      <p className="text-slate-500 text-xs">RDA</p>
                      <p className="font-medium">{selectedLaunch.rda || '—'}</p>
                    </div>
                    <div className="p-2 bg-slate-50 dark:bg-slate-800 rounded col-span-2">
                      <p className="text-slate-500 text-xs">Manufacturer Info</p>
                      <p className="font-medium whitespace-pre-wrap">{selectedLaunch.manufacturer_info || '—'}</p>
                    </div>
                    <div className="p-2 bg-slate-50 dark:bg-slate-800 rounded col-span-2">
                      <p className="text-slate-500 text-xs">Packer Info</p>
                      <p className="font-medium whitespace-pre-wrap">{selectedLaunch.packer_info || '—'}</p>
                    </div>
                    <div className="p-2 bg-slate-50 dark:bg-slate-800 rounded col-span-2">
                      <p className="text-slate-500 text-xs">Allergen Info</p>
                      <p className="font-medium whitespace-pre-wrap">{selectedLaunch.allergen_info || '—'}</p>
                    </div>
                  </div>
                </div>

                {/* Description & Bullet Points */}
                <div className="space-y-3">
                  <h4 className="font-semibold text-slate-900 dark:text-white">Description</h4>
                  <p className="text-sm text-slate-600 dark:text-slate-300 whitespace-pre-wrap">{selectedLaunch.description}</p>
                </div>

                <div className="space-y-3">
                  <h4 className="font-semibold text-slate-900 dark:text-white">Bullet Points</h4>
                  <p className="text-sm text-slate-600 dark:text-slate-300 whitespace-pre-wrap">{selectedLaunch.bullet_points}</p>
                </div>

                <div className="space-y-3">
                  <h4 className="font-semibold text-slate-900 dark:text-white">Ingredients</h4>
                  <p className="text-sm text-slate-600 dark:text-slate-300 whitespace-pre-wrap">{selectedLaunch.ingredients}</p>
                </div>

                {/* Notification Recipients */}
                {(selectedLaunch.notification_emails?.length > 0 || selectedLaunch.cc_emails?.length > 0) && (
                  <div className="space-y-3">
                    <h4 className="font-semibold text-slate-900 dark:text-white">Notifications</h4>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div className="p-2 bg-slate-50 dark:bg-slate-800 rounded">
                        <p className="text-slate-500 text-xs mb-1">Notify (To)</p>
                        <div className="space-y-0.5">
                          {(selectedLaunch.notification_emails || []).filter(Boolean).map((e, i) => (
                            <p key={i} className="font-mono text-xs">{e}</p>
                          ))}
                          {!(selectedLaunch.notification_emails || []).filter(Boolean).length && <p className="text-slate-400 text-xs">—</p>}
                        </div>
                      </div>
                      <div className="p-2 bg-slate-50 dark:bg-slate-800 rounded">
                        <p className="text-slate-500 text-xs mb-1">CC</p>
                        <div className="space-y-0.5">
                          {(selectedLaunch.cc_emails || []).filter(Boolean).map((e, i) => (
                            <p key={i} className="font-mono text-xs">{e}</p>
                          ))}
                          {!(selectedLaunch.cc_emails || []).filter(Boolean).length && <p className="text-slate-400 text-xs">—</p>}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          <DialogFooter className="border-t px-6 py-3 shrink-0">
            <Button variant="outline" onClick={() => setShowViewModal(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Platform URL Modal */}
      <Dialog open={showUrlModal} onOpenChange={setShowUrlModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <LinkIcon className="h-5 w-5 text-blue-500" />
              Add Platform URL
            </DialogTitle>
            <DialogDescription>
              Add the product listing URL once the product is live on a platform
            </DialogDescription>
          </DialogHeader>
          
          {selectedLaunch && (
            <div className="space-y-4 py-4">
              <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                <p className="text-xs text-slate-500">Product</p>
                <p className="font-medium">{selectedLaunch.amazon_title}</p>
                <p className="text-sm text-slate-500">SKU: {selectedLaunch.sku}</p>
              </div>
              
              <div className="space-y-2">
                <Label>Platform *</Label>
                <Select value={urlPlatform} onValueChange={setUrlPlatform}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select platform" />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(PLATFORM_CONFIG).map(([key, config]) => {
                      const Icon = config.icon;
                      const existingUrl = selectedLaunch[`${key}_url`];
                      return (
                        <SelectItem key={key} value={key} disabled={!!existingUrl}>
                          <div className="flex items-center gap-2">
                            <Icon className="h-4 w-4" />
                            {config.label}
                            {existingUrl && <span className="text-green-500 text-xs">(Already added)</span>}
                          </div>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Product URL *</Label>
                <Input
                  value={urlValue}
                  onChange={(e) => setUrlValue(e.target.value)}
                  placeholder="https://..."
                />
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowUrlModal(false); setUrlPlatform(''); setUrlValue(''); }}>
              Cancel
            </Button>
            <Button onClick={handleUpdateUrl} className="bg-blue-600 hover:bg-blue-700">
              <LinkIcon className="h-4 w-4 mr-2" />
              Add URL
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
